import { checklistValuesToDraft } from '@/services/checklist/checklistObservations';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Platform, SafeAreaView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { observer } from '@legendapp/state/react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { v4 as uuidv4 } from 'uuid';
import theme from '@/config/theme';
import { APP_CONSTANTS } from '@/config/constants';
import { supabase } from '@/services/supabase';
import { globalState } from '@/state/globalState';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { useWebAlert } from '@/components/WebAlert';
import { DevChecklistEditorLogger } from '@/utils/logger';
import { DevChecklistFieldsForm } from '@/components/checklist/dev/DevChecklistFieldsForm';
import { deleteDevChecklistImage } from '@/services/dev/devChecklistImageService';

type Checklist = {
  id: string;
  empresa_id: string;
  template_id: string;
  status: 'in_progress' | 'done';
  data_conclusao?: string | number | null;
  data_criacao?: string | number | null;
  sequencial_codigo?: string | null;
  sequencial_numero?: number | null;
};

type ChecklistField = {
  id: string;
  grupo: string;
  nome: string;
  tipo: string;
  ordem: number;
};

type ChecklistValue = {
  id: string;
  campo_id: string;
  valor: string | null;
  observacao?: string | null;
};

const logger = DevChecklistEditorLogger.Main;
const dataLogger = DevChecklistEditorLogger.Data;
const saveLogger = DevChecklistEditorLogger.Save;

export const DEV_CHECKLIST_EDITOR_READ_ONLY = true;

const normalizeTimestamp = (value?: string | number | null): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDateTime = (value?: string | number | null): string => {
  const normalized = normalizeTimestamp(value);
  if (!normalized) return '—';
  return new Date(normalized).toLocaleString('pt-BR');
};

const DevChecklistEditorScreen = observer(() => {
  const router = useRouter();
  const { id, returnTo } = useLocalSearchParams();
  const { showAlert } = useWebAlert();
  const userProfile = globalState.user.profile.get();
  const isDeveloper = hasDeveloperBypass(userProfile?.user_type);
  const isConnected = globalState.network.isConnected.get();

  const checklistId = Array.isArray(id) ? id[0] : id;
  const resolvedReturnTo = Array.isArray(returnTo) ? returnTo[0] : returnTo;

  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [template, setTemplate] = useState<ChecklistField[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const valueIdByCampoRef = useRef<Record<string, string>>({});
  const dirtyRef = useRef<Record<string, string>>({});
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isFlushing = useRef(false);

  useEffect(() => {
    if (!isDeveloper) {
      const message = 'Acesso restrito: somente usuarios com perfil de desenvolvedor podem abrir este painel.';
      if (Platform.OS !== 'android') {
        showAlert('Acesso negado', message, [{ text: 'Voltar', onPress: () => router.back() }]);
      } else {
        Alert.alert('Acesso negado', message, [{ text: 'OK', onPress: () => router.back() }]);
      }
    }
  }, [isDeveloper, router, showAlert]);

  const loadChecklist = useCallback(async () => {
    if (!checklistId) return;
    setLoading(true);
    setError(null);
    dataLogger.info(`[dev-checklist-editor] Carregando checklist ${checklistId}`);
    try {
      const { data: checklistData, error: checklistError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME)
        .select('*')
        .eq('id', checklistId)
        .single();

      if (checklistError) throw checklistError;

      const { data: fieldsData, error: fieldsError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME)
        .select('id, grupo, nome, tipo, ordem')
        .eq('template_id', checklistData.template_id)
        .order('ordem', { ascending: true });

      if (fieldsError) throw fieldsError;

      const { data: valuesData, error: valuesError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME)
        .select('id, campo_id, valor, observacao')
        .eq('checklist_id', checklistId);

      if (valuesError) throw valuesError;

      const valueMap = checklistValuesToDraft(valuesData as ChecklistValue[]);
      const idMap: Record<string, string> = {};
      (valuesData as ChecklistValue[]).forEach((item) => {
        if (item?.campo_id) {
          idMap[item.campo_id] = item.id;
        }
      });

      valueIdByCampoRef.current = idMap;
      setChecklist(checklistData as Checklist);
      setTemplate(fieldsData as ChecklistField[]);
      setDraft(valueMap);
      setLastSavedAt(new Date());
      dataLogger.info(`[dev-checklist-editor] Checklist ${checklistId} carregado`);
    } catch (err) {
      logger.error('[dev-checklist-editor] Erro ao carregar checklist', err);
      setError('Erro ao carregar checklist.');
    } finally {
      setLoading(false);
    }
  }, [checklistId]);

  useEffect(() => {
    if (isDeveloper && isConnected) {
      loadChecklist();
    }
  }, [isDeveloper, isConnected, loadChecklist]);

  const persistChanges = useCallback(async () => {
    if (DEV_CHECKLIST_EDITOR_READ_ONLY || isFlushing.current) return;
    const changes = { ...dirtyRef.current };
    if (Object.keys(changes).length === 0 || !checklist) {
      return;
    }
    if (!isConnected) {
      saveLogger.warn('[dev-checklist-editor] Sem conexão, adiando persistência');
      return;
    }

    isFlushing.current = true;
    setSaving(true);
    dirtyRef.current = {};
    const now = new Date().toISOString();

    try {
      for (const [campoId, valor] of Object.entries(changes)) {
        const existingId = valueIdByCampoRef.current[campoId];
        if (existingId) {
          const { error: updateError } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME)
            .update({ valor, updated_at: now })
            .eq('id', existingId);
          if (updateError) throw updateError;
        } else {
          const newId = uuidv4();
          const { error: insertError } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME)
            .insert({
              id: newId,
              empresa_id: checklist.empresa_id,
              checklist_id: checklist.id,
              campo_id: campoId,
              valor,
              created_at: now,
              updated_at: now,
            });
          if (insertError) throw insertError;
          valueIdByCampoRef.current[campoId] = newId;
        }
      }

      await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME)
        .update({ updated_at: now })
        .eq('id', checklist.id);

      setLastSavedAt(new Date());
      saveLogger.info('[dev-checklist-editor] Alterações salvas com sucesso');
    } catch (err) {
      saveLogger.error('[dev-checklist-editor] Falha ao salvar alterações', err);
      setError('Falha ao salvar alterações.');
    } finally {
      setSaving(false);
      isFlushing.current = false;
    }
  }, [checklist, isConnected]);

  const schedulePersist = useCallback(() => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current);
    }
    flushTimer.current = setTimeout(persistChanges, 800);
  }, [persistChanges]);

  const handleFieldChange = useCallback((campoId: string, value: string, options?: { immediate?: boolean }) => {
    if (DEV_CHECKLIST_EDITOR_READ_ONLY) return;
    setDraft(prev => ({ ...prev, [campoId]: value }));
    dirtyRef.current[campoId] = value;
    if (options?.immediate) {
      persistChanges();
      return;
    }
    schedulePersist();
  }, [persistChanges, schedulePersist]);

  useEffect(() => {
    return () => {
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
      }
      void persistChanges();
    };
  }, [persistChanges]);

  const handleCompleteChecklist = async () => {
    if (DEV_CHECKLIST_EDITOR_READ_ONLY) return;
    if (!checklist) return;
    setSaving(true);
    const now = new Date().toISOString();
    try {
      const { error: updateError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME)
        .update({ status: 'done', data_conclusao: now, updated_at: now })
        .eq('id', checklist.id);
      if (updateError) throw updateError;
      setChecklist(prev => prev ? { ...prev, status: 'done', data_conclusao: now } : prev);
    } catch (err) {
      saveLogger.error('[dev-checklist-editor] Falha ao concluir checklist', err);
      setError('Falha ao concluir checklist.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenChecklist = async () => {
    if (DEV_CHECKLIST_EDITOR_READ_ONLY) return;
    if (!checklist) return;
    setSaving(true);
    const now = new Date().toISOString();
    try {
      const { error: updateError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME)
        .update({ status: 'in_progress', data_conclusao: null, updated_at: now })
        .eq('id', checklist.id);
      if (updateError) throw updateError;
      setChecklist(prev => prev ? { ...prev, status: 'in_progress', data_conclusao: null } : prev);
    } catch (err) {
      saveLogger.error('[dev-checklist-editor] Falha ao reabrir checklist', err);
      setError('Falha ao reabrir checklist.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChecklist = async () => {
    if (DEV_CHECKLIST_EDITOR_READ_ONLY) return;
    if (!checklist) return;
    const confirmDelete = () => {
      const message = 'Tem certeza que deseja excluir este checklist? Esta ação não pode ser desfeita.';
      if (Platform.OS !== 'android') {
        showAlert('Excluir checklist', message, [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Excluir', style: 'destructive', onPress: () => executeDelete() },
        ]);
      } else {
        Alert.alert('Excluir checklist', message, [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Excluir', style: 'destructive', onPress: () => executeDelete() },
        ]);
      }
    };

    const executeDelete = async () => {
      setSaving(true);
      try {
        const { data: valuesData } = await supabase
          .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME)
          .select('id, valor')
          .eq('checklist_id', checklist.id);

        for (const item of (valuesData ?? []) as ChecklistValue[]) {
          if (item?.valor && (item.valor.startsWith('http://') || item.valor.startsWith('https://'))) {
            await deleteDevChecklistImage(item.valor);
          }
        }

        await supabase
          .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME)
          .delete()
          .eq('checklist_id', checklist.id);

        const { error: deleteError } = await supabase
          .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME)
          .delete()
          .eq('id', checklist.id);

        if (deleteError) throw deleteError;

        if (typeof resolvedReturnTo === 'string' && resolvedReturnTo.length > 0) {
          router.replace(resolvedReturnTo);
        } else {
          router.back();
        }
      } catch (err) {
        saveLogger.error('[dev-checklist-editor] Falha ao excluir checklist', err);
        setError('Falha ao excluir checklist.');
      } finally {
        setSaving(false);
      }
    };

    confirmDelete();
  };

  const headerSubtitle = useMemo(() => {
    if (!checklist) return '';
    const sequential = checklist.sequencial_codigo?.trim()
      || (typeof checklist.sequencial_numero === 'number' ? `Checklist #${checklist.sequencial_numero}` : 'Sem numeração');
    return sequential;
  }, [checklist]);

  if (!isDeveloper) {
    return null;
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => {
            if (typeof resolvedReturnTo === 'string' && resolvedReturnTo.length > 0) {
              router.replace(resolvedReturnTo);
            } else {
              router.back();
            }
          }}
        >
          <MaterialCommunityIcons name="chevron-left" size={22} color={theme.colors.text.primary} />
          <Text style={styles.backText}>Voltar</Text>
        </TouchableOpacity>
        {saving ? (
          <View style={styles.savingBadge}>
            <ActivityIndicator size="small" color={theme.colors.primary.default} />
            <Text style={styles.savingText}>Salvando...</Text>
          </View>
        ) : lastSavedAt ? (
          <Text style={styles.savedText}>Salvo às {lastSavedAt.toLocaleTimeString('pt-BR')}</Text>
        ) : null}
      </View>

      <View style={styles.titleRow}>
        <View>
          <Text style={styles.title}>Checklist Dev</Text>
          <Text style={styles.subtitle}>{headerSubtitle}</Text>
        </View>
        {checklist ? (
          <View style={styles.statusBadge}>
            <Text style={styles.statusText}>{checklist.status === 'done' ? 'Concluído' : 'Em andamento'}</Text>
          </View>
        ) : null}
      </View>

      {!isConnected ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="wifi-off" size={18} color={theme.colors.util.error} />
          <Text style={styles.alertText}>Sem conexão. Este editor funciona apenas online.</Text>
        </View>
      ) : null}

      {DEV_CHECKLIST_EDITOR_READ_ONLY ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="eye-outline" size={18} color={theme.colors.text.secondary} />
          <Text style={styles.alertText}>Editor em somente leitura. Alterações e ações do checklist estão temporariamente desabilitadas.</Text>
        </View>
      ) : null}

      {error ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="alert-circle-outline" size={18} color={theme.colors.util.error} />
          <Text style={styles.alertText}>{error}</Text>
        </View>
      ) : null}

      {loading ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando checklist...</Text>
        </View>
      ) : checklist ? (
        <View style={styles.formContainer}>
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>Criado em {formatDateTime(checklist.data_criacao)}</Text>
            <Text style={styles.metaText}>Última conclusão {formatDateTime(checklist.data_conclusao)}</Text>
          </View>
          <DevChecklistFieldsForm
            checklistId={checklist.id}
            empresaId={checklist.empresa_id}
            template={template}
            draft={draft}
            onChange={handleFieldChange}
            readOnly={DEV_CHECKLIST_EDITOR_READ_ONLY}
          />
        </View>
      ) : (
        <View style={styles.loadingBox}>
          <Text style={styles.loadingText}>Checklist não encontrado.</Text>
        </View>
      )}

      {checklist ? (
        <View style={styles.actions}>
          {checklist.status === 'done' ? (
            <TouchableOpacity style={styles.secondaryAction} onPress={handleReopenChecklist} disabled={DEV_CHECKLIST_EDITOR_READ_ONLY || saving}>
              <Text style={styles.secondaryActionText}>Reabrir</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={styles.primaryAction} onPress={handleCompleteChecklist} disabled={DEV_CHECKLIST_EDITOR_READ_ONLY || saving}>
              <Text style={styles.primaryActionText}>Concluir</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={styles.deleteAction} onPress={handleDeleteChecklist} disabled={DEV_CHECKLIST_EDITOR_READ_ONLY || saving}>
            <Text style={styles.deleteActionText}>Excluir checklist</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </SafeAreaView>
  );
});

export default DevChecklistEditorScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    paddingHorizontal: theme.spacing.lg,
    paddingTop: theme.spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  backText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  savingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  savingText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  savedText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  titleRow: {
    paddingHorizontal: theme.spacing.lg,
    paddingTop: theme.spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: theme.fontSizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.text.primary,
  },
  subtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  statusBadge: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: 4,
    backgroundColor: theme.colors.background.primary,
  },
  statusText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.primary,
  },
  alertCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.util.error + '22',
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginHorizontal: theme.spacing.lg,
    marginTop: theme.spacing.md,
  },
  alertText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    flex: 1,
  },
  loadingBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
  },
  loadingText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  formContainer: {
    flex: 1,
    marginTop: theme.spacing.sm,
  },
  metaRow: {
    paddingHorizontal: theme.spacing.lg,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
  },
  metaText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  actions: {
    padding: theme.spacing.lg,
    gap: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
  },
  primaryAction: {
    backgroundColor: theme.colors.primary.default,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
  },
  primaryActionText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600' as any,
  },
  secondaryAction: {
    backgroundColor: theme.colors.background.secondary,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  secondaryActionText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600' as any,
  },
  deleteAction: {
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.util.error,
  },
  deleteActionText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600' as any,
  },
});
